// =============================================================================
// screens/LegalScreen.tsx — IN-APP Terms / Privacy / Guidelines (#4).
//
// Apple 1.2 / 5.1.1 require the legal docs to be reachable IN THE APP, not only
// via an external browser. This renders the same copy served at
// platform.cryb.ai/{terms,privacy,guidelines} (source of truth:
// apps/react-app/.../CRYB_09B_LegalErrorMisc.jsx) as native scrollable content —
// no WebView dependency. Keep the section text in sync with the web pages.
//
// APPLE-ALIGNED REVAMP (matches Home/Search/Notifications/DM/Communities):
//   • Full-bleed ParticleBackground aurora under the status bar (subtle, per the
//     document-screen note); screen itself is transparent so the light reads
//     through the reading column.
//   • Native stack header hidden → an in-screen TRANSLUCENT glass header (back +
//     centered title) with content scrolling UNDER it, plus a scroll-edge fade
//     where content meets the floating chrome (skill §12).
//   • Display title with negative tracking, lede + "last updated" caption
//     (typography hierarchy from weight+size, skill §15).
//   • An in-app doc switcher (Terms · Privacy · Guidelines) as glass pills so the
//     three legal docs are reachable without leaving the screen — respond on
//     press via the shared GlassPill spring.
//   • Readable near-solid glass section cards preserved for legibility.
// =============================================================================
import { useLayoutEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import type { RootStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, getLayout } from '../constants/theme';
import { GlassCard, GlassSurface, GlassPill } from '../components/glass';
import { Ionicons } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';

type Nav = NativeStackNavigationProp<RootStackParamList, 'Legal'>;
type LegalRoute = RouteProp<RootStackParamList, 'Legal'>;

type LegalDoc = 'terms' | 'privacy' | 'guidelines';
interface Section { title: string; paragraphs: string[] }
interface LegalContent { title: string; tagline: string; updated: string; sections: Section[] }

const UPDATED = 'May 27, 2026';

const TERMS: Section[] = [
  { title: '1. Acceptance of Terms', paragraphs: [
    'By accessing or using the CRYB platform, you agree to be bound by these Terms of Service. If you do not agree, do not use CRYB. These terms apply to all users, including registered members and guests.',
    'CRYB is provided as a native mobile app and on desktop web. The service is not available through mobile web browsers — on a phone you will be directed to download the app. We may enable, disable, or rate-limit features (such as sign-ups, posting, comments, communities, group chats, and trading) at our discretion to protect the platform and its users.',
  ] },
  { title: '2. Eligibility', paragraphs: [
    'You must be at least 13 years old (or the minimum age in your jurisdiction) to use CRYB. By using the platform, you represent that you meet this requirement. Users under 18 must have parental or guardian consent.',
  ] },
  { title: '3. User Accounts', paragraphs: [
    'You are responsible for maintaining the confidentiality of your credentials and all activities under your account. Notify us immediately at security@cryb.ai if you suspect unauthorized access.',
    'You may not create accounts for others without authorization, use automated means to create accounts, or impersonate any person or entity.',
  ] },
  { title: '4. Content Ownership', paragraphs: [
    'You retain ownership of content you create on CRYB. By posting content, you grant CRYB a non-exclusive, worldwide, royalty-free license to display, distribute, and promote your content on the platform and in marketing materials.',
    'You represent that you own or have the necessary rights to all content you post and that it does not infringe third-party rights.',
  ] },
  { title: '5. Prohibited Conduct', paragraphs: [
    'You agree not to: violate laws or regulations; infringe intellectual property rights; transmit malware or harmful code; attempt to gain unauthorized access to systems; harass, abuse, or threaten others; manipulate markets or engage in fraud; circumvent platform features or access controls.',
  ] },
  { title: '6. Web3 & Financial Features', paragraphs: [
    'CRYB provides tools for connecting wallets, viewing NFTs, and participating in on-chain activities. These features are provided as-is. CRYB is not a financial service, exchange, or broker. All crypto transactions are your own responsibility.',
    'Token values are highly volatile. Never invest more than you can afford to lose. Content on CRYB is not financial advice.',
  ] },
  { title: '7. Termination', paragraphs: [
    'CRYB may suspend or terminate your account at any time for violations of these terms. You may delete your account at any time in settings. Termination does not affect rights or obligations accrued before termination.',
  ] },
  { title: '8. Limitation of Liability', paragraphs: [
    'CRYB is provided "as is" without warranties of any kind. To the fullest extent permitted by law, CRYB is not liable for indirect, incidental, special, or consequential damages, including loss of funds from crypto activities conducted through the platform.',
  ] },
  { title: '9. Governing Law', paragraphs: [
    'These terms are governed by the laws of the jurisdiction in which CRYB Platform Ltd. is incorporated. Disputes shall be resolved through binding arbitration, except where prohibited by law.',
  ] },
  { title: '10. Changes to Terms', paragraphs: [
    'We may update these terms. Continued use after changes constitutes acceptance. Significant changes will be notified via email or in-app notification with at least 14 days notice.',
  ] },
];

const PRIVACY: Section[] = [
  { title: '1. Information We Collect', paragraphs: [
    'We collect information you provide directly: account details (username, email, avatar), content you post (messages, posts, reactions), and wallet addresses you connect to the platform.',
    'We also collect usage data automatically: IP addresses, device type, browser, pages visited, timestamps, and interaction patterns. This helps us improve performance and detect abuse.',
  ] },
  { title: '2. How We Use Your Information', paragraphs: [
    'Your data is used to provide and improve the CRYB platform, personalize your experience, send notifications you have opted into, enforce our community guidelines, and comply with legal obligations.',
    'We do not sell your personal information to third parties. We may share anonymized, aggregated analytics data for research purposes.',
  ] },
  { title: '3. Wallet & Blockchain Data', paragraphs: [
    'Wallet addresses and on-chain activity are public by nature of blockchain technology. We display this data on your profile if you choose to connect a wallet. We do not control blockchain data and cannot delete it.',
  ] },
  { title: '4. Data Retention', paragraphs: [
    'We retain your account data for as long as your account is active. Upon deletion, personal data is removed within 30 days, except where retention is required by law.',
  ] },
  { title: '5. Cookies & Tracking', paragraphs: [
    'We use essential cookies for session management and authentication. Analytics cookies are only set with your consent. You can manage cookie preferences in your browser or account settings.',
  ] },
  { title: '6. Your Rights (GDPR / CCPA)', paragraphs: [
    'Depending on your jurisdiction, you may have rights to: access your data, correct inaccuracies, request deletion, restrict processing, data portability, and opt out of certain uses. Submit requests to privacy@cryb.ai.',
  ] },
  { title: '7. Security', paragraphs: [
    'We implement industry-standard security measures including TLS encryption, hashed passwords, rate limiting, and regular security audits. However, no system is 100% secure; use strong, unique passwords and enable 2FA.',
  ] },
  { title: '8. Contact', paragraphs: [
    'For privacy inquiries, contact our Data Protection Officer at privacy@cryb.ai or write to CRYB Platform Ltd., Data Privacy Team.',
  ] },
];

const GUIDELINES: Section[] = [
  { title: '1. Be Respectful', paragraphs: [
    'Treat all community members with respect and dignity. Harassment, hate speech, discrimination, and personal attacks are strictly prohibited. This includes content targeting individuals based on race, gender, sexual orientation, religion, nationality, or disability.',
    'Disagreements are natural — debate ideas, not people. Constructive criticism is welcome; personal insults are not.',
  ] },
  { title: '2. No Spam or Self-Promotion', paragraphs: [
    'Do not flood channels with repetitive messages, unsolicited promotions, or affiliate links. Share valuable content that contributes to the conversation. Excessive self-promotion without community engagement will result in content removal.',
  ] },
  { title: '3. No Financial Advice', paragraphs: [
    'CRYB is a social platform, not a financial advisory service. Do not present speculative investment ideas as guaranteed returns, and always include disclaimers when discussing assets. We are not liable for financial decisions made based on platform content.',
  ] },
  { title: '4. No Market Manipulation', paragraphs: [
    'Coordinated pump-and-dump schemes, wash trading discussions, or deliberate spread of false information to influence token prices are strictly prohibited and may be reported to relevant authorities.',
  ] },
  { title: '5. Protect Privacy', paragraphs: [
    'Do not share personally identifiable information (PII) of others without their consent. This includes wallet addresses used to dox individuals, private messages, photos, or any other private data.',
  ] },
  { title: '6. No Malicious Content', paragraphs: [
    'Sharing malware, phishing links, scam contracts, honeypot tokens, or any other content designed to defraud or harm users is grounds for immediate and permanent ban.',
  ] },
  { title: '7. NSFW Content', paragraphs: [
    'Explicit adult content is only permitted in age-gated, designated communities where it is explicitly allowed. Never post such content in general communities or channels.',
  ] },
  { title: '8. Enforcement', paragraphs: [
    'Violations may result in content removal, temporary suspension, or permanent ban depending on severity. You can appeal moderation decisions through the support center. CRYB reserves the right to remove any content at its discretion.',
  ] },
];

const CONTENT: Record<LegalDoc, LegalContent> = {
  terms: {
    title: 'Terms of Service',
    tagline: 'The agreement between you and CRYB. Please read it before using the platform.',
    updated: UPDATED, sections: TERMS,
  },
  privacy: {
    title: 'Privacy Policy',
    tagline: 'What we collect, why we collect it, and the control you keep over your data.',
    updated: UPDATED, sections: PRIVACY,
  },
  guidelines: {
    title: 'Community Guidelines',
    tagline: 'The house rules that keep CRYB safe, respectful, and worth showing up for.',
    updated: UPDATED, sections: GUIDELINES,
  },
};

// Doc switcher — short pill labels + the icon that reads the doc's intent.
const DOCS: { key: LegalDoc; label: string; icon: string }[] = [
  { key: 'terms', label: 'Terms', icon: 'document-text-outline' },
  { key: 'privacy', label: 'Privacy', icon: 'lock-closed-outline' },
  { key: 'guidelines', label: 'Guidelines', icon: 'people-outline' },
];

export default function LegalScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<LegalRoute>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle =
    layout !== 'phone'
      ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
      : undefined;
  const doc = (route.params?.doc ?? 'terms') as LegalDoc;
  const content = CONTENT[doc] ?? CONTENT.terms;

  // Self-managed translucent header → hide the opaque native stack bar so the
  // aurora runs full-bleed under the status bar (skill §12). No gap left behind.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const headerH = insets.top + 52;

  return (
    <View style={styles.screen}>
      {/* Subtle full-bleed aurora — appropriate weight for a reading document. */}
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={4} intensity={0.7} />

      <ScrollView
        contentContainerStyle={[
          styles.content,
          containerStyle,
          { paddingTop: headerH + spacing.md, paddingBottom: insets.bottom + spacing.xxxl },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Document masthead — display title with tight tracking, lede, stamp. */}
        <Text style={styles.title}>{content.title}</Text>
        <Text style={styles.tagline}>{content.tagline}</Text>

        <View style={styles.stampRow}>
          <View style={styles.stampDot} />
          <Text style={styles.updated}>Last updated {content.updated}</Text>
        </View>

        {/* In-app doc switcher — reach all three legal docs without leaving. */}
        <View style={styles.switcher}>
          {DOCS.map((d) => (
            <GlassPill
              key={d.key}
              label={d.label}
              icon={d.icon}
              active={d.key === doc}
              onPress={() => navigation.setParams({ doc: d.key })}
              accessibilityLabel={`Show ${CONTENT[d.key].title}`}
            />
          ))}
        </View>

        {content.sections.map((s) => (
          <GlassCard key={s.title} style={styles.card} padding={spacing.lg}>
            <Text style={styles.sectionTitle}>{s.title}</Text>
            {s.paragraphs.map((p, i) => (
              <Text key={i} style={[styles.para, i > 0 && styles.paraGap]}>{p}</Text>
            ))}
          </GlassCard>
        ))}

        {/* Closing note — the docs also live on the web; no dead end. */}
        <Text style={styles.footNote}>
          These terms are also available at platform.cryb.ai. Questions? Reach us
          at privacy@cryb.ai.
        </Text>
      </ScrollView>

      {/* Scroll-edge fade where content meets the floating header — a soft mask,
          not a hard divider (skill §12). */}
      <LinearGradient
        pointerEvents="none"
        colors={[colors.bg, 'rgba(232,225,242,0)']}
        style={[styles.edgeFade, { top: headerH, height: spacing.xl }]}
      />

      {/* Translucent in-screen header: back + centered title, content scrolls
          under it. Never an opaque bar with a gap. */}
      <GlassSurface
        variant="bar"
        radius={0}
        sheen={false}
        style={[styles.header, { paddingTop: insets.top, height: headerH }]}
        contentStyle={styles.headerInner}
      >
        <TouchableOpacity
          style={styles.backBtn}
          onPress={() => navigation.goBack()}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Go back"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="chevron-back" size={24} color={colors.ink} />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>{content.title}</Text>
        <View style={styles.backBtn} />
      </GlassSurface>
    </View>
  );
}

const styles = StyleSheet.create({
  // Transparent so the ParticleBackground aurora reads through the status bar
  // and (via the translucent surfaces) the reading column.
  screen: { flex: 1, backgroundColor: 'transparent' },
  content: { paddingHorizontal: spacing.lg },

  // Floating translucent header (skill §12).
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
  },
  headerInner: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
  },
  backBtn: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    ...fonts.semibold,
    fontSize: 17,
    letterSpacing: -0.2,
    color: colors.ink,
  },
  edgeFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 19,
  },

  // Masthead
  title: {
    ...fonts.extrabold,
    fontSize: 32,
    lineHeight: 38,
    letterSpacing: -0.6,
    color: colors.ink,
    marginBottom: spacing.xs,
  },
  tagline: {
    ...fonts.medium,
    fontSize: 16,
    lineHeight: 23,
    color: colors.ink2,
    marginBottom: spacing.md,
  },
  stampRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
    marginBottom: spacing.lg,
  },
  stampDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary,
  },
  updated: {
    ...fonts.medium,
    fontSize: 12.5,
    letterSpacing: 0.2,
    color: colors.muted,
  },

  // Doc switcher
  switcher: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.xl,
  },

  // Section cards — near-solid glass for reading comfort.
  card: { marginBottom: spacing.md, borderRadius: radius.lg },
  sectionTitle: {
    ...fonts.bold,
    fontSize: 17,
    letterSpacing: -0.2,
    color: colors.ink,
    marginBottom: spacing.sm,
  },
  para: {
    ...fonts.regular,
    fontSize: 15,
    color: colors.ink2,
    lineHeight: 23,
  },
  paraGap: { marginTop: spacing.sm },

  footNote: {
    ...fonts.regular,
    fontSize: 13,
    lineHeight: 20,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
});
